import { sortOrganisations } from "@/utils/organisationSort";
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArchiveX, RefreshCw, Trash2, Battery, Signal, MapPin, Bug, ChevronDown, ChevronUp, UserX, ClipboardList } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatInTimeZone } from 'date-fns-tz';
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";
import { useRoleFlags } from "@/hooks/use-role-flags";
import DeviceCreditHistory from "@/components/devices/DeviceCreditHistory";
interface EditDeviceDetailsProps {
  deviceId: string;
  initialName: string | null;
  initialDescription: string | null;
  initialCameraImageQuality: string | null;
  initialImagesAllowed: number;
  initialImagesCurrent: number;
  initialGroupName?: string | null;
  initialGroupReference?: string | null;
  onSave: () => void;
  onCancel: () => void;
  onViewFullDetails?: () => void;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
}
export const EditDeviceDetails = ({
  deviceId,
  initialName,
  initialDescription,
  initialCameraImageQuality,
  initialImagesAllowed,
  initialImagesCurrent = 0,
  initialGroupName,
  initialGroupReference,
  onSave,
  onCancel,
  onViewFullDetails,
  imageUrl,
  thumbnailUrl
}: EditDeviceDetailsProps) => {
  const { logChange } = useDeviceChangeLog();
  const { isSuperuser } = useRoleFlags();
  const [deviceName, setDeviceName] = useState(initialName || "");
  const [cameraImageQuality, setCameraImageQuality] = useState(initialCameraImageQuality || "");
  const [imagesAllowed, setImagesAllowed] = useState(initialImagesAllowed?.toString() || "30");
  const NO_ORG = "__none__";
  const [organizationId, setOrganizationId] = useState<string>(NO_ORG);
  const [initialOrganizationId, setInitialOrganizationId] = useState<string>(NO_ORG);

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations-select-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data || []) as Array<{ id: string; name: string }>;
    },
    enabled: isSuperuser,
  });
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isDeleteFileDialogOpen, setIsDeleteFileDialogOpen] = useState(false);
  const [deviceData, setDeviceData] = useState<any>(null);
  const [enableDebug, setEnableDebug] = useState(false);
  const [isDebugExpanded, setIsDebugExpanded] = useState(false);
  const [isUpdatingDebug, setIsUpdatingDebug] = useState(false);
  const [isPortalLogExpanded, setIsPortalLogExpanded] = useState(false);
  const [isLoadingPortalLogs, setIsLoadingPortalLogs] = useState(false);
  const [portalLogs, setPortalLogs] = useState<Array<{
    field: string;
    value: string | null;
    created_at: string;
    user_name: string;
    org_name: string;
  }>>([]);
  const [archiveConfirmationText, setArchiveConfirmationText] = useState("");
  const [isRemoveAssignmentsDialogOpen, setIsRemoveAssignmentsDialogOpen] = useState(false);
  const [removeAssignmentsConfirmationText, setRemoveAssignmentsConfirmationText] = useState("");
  const [isRemovingAssignments, setIsRemovingAssignments] = useState(false);
  
  const queryClient = useQueryClient();
  
  // Debug API states
  const [isLoadingDebug, setIsLoadingDebug] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);
  const [lastDebugStatus, setLastDebugStatus] = useState<number | null>(null);
  const [lastDebugRaw, setLastDebugRaw] = useState<string | null>(null);
  const [lastDebugAt, setLastDebugAt] = useState<string | null>(null);
  const [parsedDebugData, setParsedDebugData] = useState<Array<{timestamp: string, message: string}>>([]);
  useEffect(() => {
    // Fetch the device details to get battery and other data
    const fetchDeviceData = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('device_details').select('battery, cellular, lat_wgs84, long_wgs84, current_hdop, camera_config, gnss, organization_id').eq('device_id', deviceId).single();
        if (error) throw error;
        console.log("Device data fetched:", data);
        setDeviceData(data);
        setOrganizationId((data as any)?.organization_id || NO_ORG);
        setInitialOrganizationId((data as any)?.organization_id || NO_ORG);
        
        // Parse camera_config to get enableDebug
        if (data?.camera_config) {
          let cameraConfig;
          try {
            cameraConfig = typeof data.camera_config === 'string' 
              ? JSON.parse(data.camera_config) 
              : data.camera_config;
          } catch (e) {
            console.error("Error parsing camera_config:", e);
            cameraConfig = {};
          }
          setEnableDebug(cameraConfig?.enableDebug === 1 || cameraConfig?.enableDebug === "1");
        }
      } catch (error) {
        console.error("Error fetching device data:", error);
      }
    };

    fetchDeviceData();
  }, [deviceId]);

  // Log the actual camera image quality value
  useEffect(() => {
    console.log("Initial camera image quality value:", initialCameraImageQuality);
  }, [initialCameraImageQuality]);

  const handleDebugToggle = async (checked: boolean) => {
    console.log('🔄 [EditDeviceDetails] Debug toggle requested:', checked);
    setIsUpdatingDebug(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");

      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", "cameraConfig.enableDebug");
      params.append("value", checked ? "1" : "0");
      
      console.log('🔄 [EditDeviceDetails] Updating debug via API:', deviceId, checked ? "1" : "0");
      logChange(deviceId, "cameraConfig.enableDebug", checked ? "1" : "0");
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      });

      if (!response.ok) {
        console.error('Failed to update debug setting:', await response.text());
        throw new Error("Failed to update debug setting");
      }

      // Re-fetch device data to get the persisted value
      const { data, error } = await supabase
        .from('device_details')
        .select('camera_config')
        .eq('device_id', deviceId)
        .single();
        
      if (error) throw error;
      
      // Parse and set the persisted value
      if (data?.camera_config) {
        let cameraConfig;
        try {
          cameraConfig = typeof data.camera_config === 'string' 
            ? JSON.parse(data.camera_config) 
            : data.camera_config;
        } catch (e) {
          console.error("Error parsing camera_config:", e);
          cameraConfig = {};
        }
        const persistedValue = cameraConfig?.enableDebug === 1 || cameraConfig?.enableDebug === "1";
        console.log('✅ [EditDeviceDetails] Debug setting persisted as:', persistedValue);
        setEnableDebug(persistedValue);
      }

      toast({
        title: "Success",
        description: "Debug setting updated"
      });
    } catch (error) {
      console.error("Error updating debug setting:", error);
      // Revert to previous state on error
      setEnableDebug(!checked);
      toast({
        title: "Error",
        description: "Failed to update debug setting",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingDebug(false);
    }
  };

  // Debug API function
  const fetchDebugData = async (targetId?: string) => {
    const deviceIdToUse = targetId || deviceId;
    console.log(`🔬 [DebugData] Starting deviceDebug API call for device: ${deviceIdToUse}`);
    
    setIsLoadingDebug(true);
    setDebugError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const headers: Record<string, string> = {
        "Content-Type": "application/json"
      };
      
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }
      
      const response = await fetch("https://i56o7w.buildship.run/deviceDebug", {
        method: "POST",
        headers,
        body: JSON.stringify({ deviceID: deviceIdToUse })
      });
      
      const responseText = await response.text();
      
      setLastDebugStatus(response.status);
      setLastDebugRaw(responseText);
      setLastDebugAt(new Date().toISOString());
      
      if (response.ok) {
        console.log(`🔬 [DebugData] deviceDebug OK for ${deviceIdToUse} (status ${response.status}): ${responseText.substring(0, 100)}...`);
        
        // Parse the JSON response and extract timestamp-message pairs
        try {
          const jsonData = JSON.parse(responseText);
          if (jsonData.data && typeof jsonData.data === 'object') {
            const entries = Object.entries(jsonData.data).map(([timestamp, message]) => ({
              timestamp,
              message: String(message)
            }));
            // Sort by timestamp descending (most recent first)
            entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            setParsedDebugData(entries);
            console.log(`🔬 [DebugData] Parsed ${entries.length} debug entries for ${deviceIdToUse}`);
          } else {
            console.warn(`🔬 [DebugData] Response missing 'data' object for ${deviceIdToUse}`);
            setParsedDebugData([]);
          }
        } catch (parseError) {
          console.error(`🔬 [DebugData] Failed to parse JSON response for ${deviceIdToUse}:`, parseError);
          setParsedDebugData([]);
        }
        
        toast({
          title: "Debug Data",
          description: "Debug data retrieved successfully"
        });
      } else {
        console.error(`❌ [DebugData] deviceDebug failed for ${deviceIdToUse} (status ${response.status}): ${responseText}`);
        setDebugError(`API returned status ${response.status}`);
        toast({
          title: "Debug Data Error",
          description: `Failed to fetch debug data (status ${response.status})`,
          variant: "destructive"
        });
      }
      
    } catch (error) {
      console.error(`❌ [DebugData] deviceDebug error for ${deviceIdToUse}:`, error);
      setDebugError(error instanceof Error ? error.message : 'Unknown error');
      toast({
        title: "Debug Data Error",
        description: "Failed to fetch debug data",
        variant: "destructive"
      });
    } finally {
      setIsLoadingDebug(false);
    }
  };

  // Portal Log fetch function
  const fetchPortalLogs = async () => {
    setIsLoadingPortalLogs(true);
    try {
      // Step 1: Fetch logs
      const { data: logs, error: logsError } = await (supabase as any)
        .from('device_change_log')
        .select('*')
        .eq('device_id', deviceId)
        .order('created_at', { ascending: false })
        .limit(40);

      if (logsError) throw logsError;
      if (!logs || logs.length === 0) {
        setPortalLogs([]);
        return;
      }

      // Step 2: Get unique user_ids and org_ids
      const userIds = [...new Set(logs.map((l: any) => l.user_id).filter(Boolean))] as string[];
      const orgIds = [...new Set(logs.map((l: any) => l.organization_id).filter(Boolean))] as string[];

      // Step 3: Batch fetch profiles and organizations
      const [profilesRes, orgsRes] = await Promise.all([
        userIds.length > 0
          ? supabase.from('profiles').select('user_id, first_name, last_name').in('user_id', userIds)
          : { data: [] },
        orgIds.length > 0
          ? supabase.from('organizations').select('id, name').in('id', orgIds)
          : { data: [] },
      ]);

      const profileMap = new Map<string, string>();
      (profilesRes.data || []).forEach((p: any) => {
        profileMap.set(p.user_id, [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unknown');
      });

      const orgMap = new Map<string, string>();
      (orgsRes.data || []).forEach((o: any) => {
        orgMap.set(o.id, o.name || 'Unknown');
      });

      // Step 4: Merge
      const enriched = logs.map((log: any) => ({
        field: log.field,
        value: log.value,
        created_at: log.created_at,
        user_name: log.user_id ? (profileMap.get(log.user_id) || 'Unknown') : 'System',
        org_name: log.organization_id ? (orgMap.get(log.organization_id) || 'Unknown') : '—',
      }));

      setPortalLogs(enriched);
    } catch (error) {
      console.error('Failed to fetch portal logs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch portal change logs",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPortalLogs(false);
    }
  };

  const isValidArchiveConfirmation = (text: string): boolean => {
    return text.toUpperCase() === "ARCHIVE" || text === deviceName;
  };

  // Validation function for remove assignments confirmation text
  const isValidRemoveAssignmentsConfirmation = (text: string): boolean => {
    return text.toUpperCase() === "REMOVE" || text === deviceName;
  };

  const handleSave = async () => {
    try {
      const updateData: any = {
        device_name: deviceName.trim() || null,
        images_allowed: Number(imagesAllowed)
      };
      if (cameraImageQuality) {
        updateData.camera_image_quality = cameraImageQuality;
      }
      const {
        error
      } = await supabase.from('device_details').update(updateData).eq('device_id', deviceId);
      if (error) throw error;

      // Update the device config via API
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");


      // Update camera image quality
      if (cameraImageQuality) {
        const paramsCameraQuality = new URLSearchParams();
        paramsCameraQuality.append("deviceID", deviceId);
        paramsCameraQuality.append("field", "cameraConfig.cameraImageQuality");
        paramsCameraQuality.append("value", cameraImageQuality);
        console.log('🔄 [EditDeviceDetails] Updating camera image quality via API:', deviceId, cameraImageQuality);
        logChange(deviceId, "cameraConfig.cameraImageQuality", cameraImageQuality);
        const responseCameraQuality = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: paramsCameraQuality
        });
        if (!responseCameraQuality.ok) {
          console.error('Failed to update camera image quality:', await responseCameraQuality.text());
          throw new Error("Failed to update camera image quality");
        }
      }

      // Update images allowed
      const paramsAllowed = new URLSearchParams();
      paramsAllowed.append("deviceID", deviceId);
      paramsAllowed.append("field", "contract.imagesAllowed");
      paramsAllowed.append("value", imagesAllowed);
      console.log('Updating images allowed via API:', deviceId, imagesAllowed);
      logChange(deviceId, "contract.imagesAllowed", imagesAllowed);
      const responseAllowed = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: paramsAllowed
      });
      if (!responseAllowed.ok) {
        console.error('Failed to update images allowed:', await responseAllowed.text());
        throw new Error("Failed to update images allowed");
      }

      // Update owning organisation (superusers only, and only when changed)
      if (isSuperuser && organizationId !== initialOrganizationId) {
        const orgValue = organizationId === NO_ORG ? "" : organizationId;
        const paramsOrg = new URLSearchParams();
        paramsOrg.append("deviceID", deviceId);
        paramsOrg.append("field", "organisationID");
        paramsOrg.append("value", orgValue);
        console.log('Updating organisation via API:', deviceId, orgValue);
        const responseOrg = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: paramsOrg
        });
        if (!responseOrg.ok) {
          console.error('Failed to update organisation:', await responseOrg.text());
          throw new Error("Failed to update organisation");
        }

        const { error: orgError } = await supabase
          .from('device_details')
          .update({ organization_id: organizationId === NO_ORG ? null : organizationId })
          .eq('device_id', deviceId);
        if (orgError) throw orgError;

        logChange(deviceId, "organisationID", orgValue);
        setInitialOrganizationId(organizationId);
        queryClient.invalidateQueries({ queryKey: ['device-details'] });
        queryClient.invalidateQueries({ queryKey: ['devices'] });
      }

      toast.success("Device details updated");
      onSave();
    } catch (error) {
      console.error("Error updating device:", error);
      toast.error("Failed to update device details");
    }
  };
  const handleArchive = async () => {
    try {
      const response = await fetch("https://i56o7w.buildship.run/device-archive", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({
          deviceID: deviceId
        })
      });
      if (!response.ok) {
        throw new Error("Failed to archive device");
      }
      toast.success("Device archived successfully");

      // Best-effort cleanup of the device's AI test image (storage + DB pointer).
      try {
        const { data: existing } = await supabase.storage
          .from('device-test-images')
          .list(deviceId);
        if (existing && existing.length > 0) {
          await supabase.storage
            .from('device-test-images')
            .remove(existing.map((f) => `${deviceId}/${f.name}`));
        }
        await supabase
          .from('device_details')
          .update({ test_image_url: null })
          .eq('device_id', deviceId);
      } catch (cleanupErr) {
        console.error('Test image cleanup failed during archive:', cleanupErr);
      }

      onSave(); // Refresh the parent component
    } catch (error) {
      console.error("Error archiving device:", error);
      toast.error("Failed to archive device");
    } finally {
      setIsArchiveDialogOpen(false);
      setArchiveConfirmationText("");
    }
  };

  const handleRemoveAssignments = async () => {
    setIsRemovingAssignments(true);
    
    try {
      // Get current user for audit trail
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;
      
      // Step 1: Delete device assignments
      const { error: assignmentsError } = await supabase
        .from('device_assignments')
        .delete()
        .eq('device_id', deviceId);
      
      if (assignmentsError) {
        console.error('Error deleting device assignments:', assignmentsError);
        throw new Error('Failed to remove device assignments');
      }
      
      // Step 2: Delete credit packages for this device
      const { error: packagesError } = await supabase
        .from('device_credit_packages')
        .delete()
        .eq('device_id', deviceId);
      
      if (packagesError) {
        console.error('Error deleting credit packages:', packagesError);
        throw new Error('Failed to remove credit packages');
      }
      
      // Step 3: Delete credit allocations for this device
      const { error: creditsError } = await supabase
        .from('device_assignment_credits')
        .delete()
        .eq('device_id', deviceId);
      
      if (creditsError) {
        console.error('Error deleting credit allocations:', creditsError);
        throw new Error('Failed to remove credit allocations');
      }
      
      // Step 3.5: Call BuildShip API to unassign device in external system
      const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('get-buildship-token');
      
      if (tokenError) {
        console.error('Failed to get BuildShip token:', tokenError);
        throw new Error('Failed to get authorization token for device unassignment');
      }

      if (!tokenResponse?.success || !tokenResponse?.token) {
        throw new Error('Failed to get valid BuildShip authorization token');
      }

      // Call BuildShip device-unassign API
      const buildshipResponse = await fetch('https://api.snapshot.systems/device-unassign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenResponse.token}`,
        },
        body: JSON.stringify({
          deviceID: deviceId,
        }),
      });

      if (!buildshipResponse.ok) {
        const errorText = await buildshipResponse.text();
        console.error('BuildShip device-unassign API error:', errorText);
        throw new Error(`Failed to unassign device in external system: ${buildshipResponse.status}`);
      }

      const buildshipResult = await buildshipResponse.json();
      console.log('BuildShip device-unassign response:', buildshipResult);

      // Step 3.6: Clear device group and organization assignment locally
      const { error: clearError } = await supabase
        .from('device_details')
        .update({
          group_id: null,
          group_name: null,
          group_reference: null,
          organization_id: null,
        })
        .eq('device_id', deviceId);

      if (clearError) {
        console.warn('Warning: Failed to clear group/organization on device_details:', clearError);
      }

      // Step 4: Add audit entry for the removal
      const { error: auditError } = await supabase
        .from('device_assignment_audit')
        .insert({
          device_id: deviceId,
          user_id: currentUserId,
          action: 'assignments_removed',
          assignment_type: 'system',
          metadata: {
            removed_at: new Date().toISOString(),
            removed_by: currentUserId,
            device_name: deviceName || deviceId,
            reason: 'Manual removal via UI',
            external_unassignment: 'success',
            buildship_api_called: true,
            group_cleared: !clearError,
            organization_cleared: !clearError,
          }
        });
      
      if (auditError) {
        console.warn('Warning: Failed to create audit entry:', auditError);
        // Don't throw - audit failure shouldn't fail the whole operation
      }
      
      // Step 5: Invalidate relevant queries to update UI
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-devices'] }),
        queryClient.invalidateQueries({ queryKey: ['customer-devices'] }),
        queryClient.invalidateQueries({ queryKey: ['device-assignments'] }),
        queryClient.invalidateQueries({ queryKey: ['credit-packages'] }),
        queryClient.invalidateQueries({ queryKey: ['device-details', deviceId] }),
        queryClient.invalidateQueries({ queryKey: ['device-groups-with-counts'] }),
        queryClient.invalidateQueries({ queryKey: ['device-groups-scoped-with-counts'] }),
      ]);
      
      toast({
        title: "Assignments Removed",
        description: `All assignments, credit packages, and allocations have been removed for ${deviceName || deviceId}. Device unassigned in external system.`,
      });
      
      onSave(); // Refresh the parent component
      
    } catch (error) {
      console.error("Error removing assignments:", error);
      toast({
        title: "Failed to Remove Assignments",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsRemovingAssignments(false);
      setIsRemoveAssignmentsDialogOpen(false);
      setRemoveAssignmentsConfirmationText("");
    }
  };
  const handleResetCounter = async () => {
    try {
      // Direct database update to reset images_current to 0
      const {
        error: dbError
      } = await supabase.from('device_details').update({ 
        images_current: 0 
      }).eq('device_id', deviceId);
      if (dbError) throw dbError;

      // Now update the contract via API
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");
      const paramsCurrent = new URLSearchParams();
      paramsCurrent.append("deviceID", deviceId);
      paramsCurrent.append("field", "contract.imagesCurrent");
      paramsCurrent.append("value", "0");
      console.log('Resetting images counter to 0 via API:', deviceId);
      logChange(deviceId, "contract.imagesCurrent", "0");
      const responseCurrent = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: paramsCurrent
      });
      if (!responseCurrent.ok) {
        console.error('Failed to reset images counter:', await responseCurrent.text());
        throw new Error("Failed to reset images counter");
      }
      toast.success("Image counter reset to 0");
      onSave(); // Refresh the parent component
    } catch (error) {
      console.error("Error resetting image counter:", error);
      toast.error("Failed to reset image counter");
    } finally {
      setIsResetDialogOpen(false);
    }
  };
  const handleDeleteSdCardFiles = async () => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No session found");
      }
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", "cameraConfig.purgeSdCard");
      params.append("value", "1");
      logChange(deviceId, "cameraConfig.purgeSdCard", "1");
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      });
      if (!response.ok) {
        throw new Error("Failed to purge device SD card");
      }
      toast.success("Device SD card purge initiated");
      onSave(); // Refresh the parent component
    } catch (error) {
      console.error("Error purging SD card:", error);
      toast.error("Failed to purge device SD card");
    } finally {
      setIsDeleteFileDialogOpen(false);
    }
  };

  // Helper function to convert cellular RAT to network type
  const getCellularNetworkType = (rat: string) => {
    const types: {
      [key: string]: string;
    } = {
      "1": "GSM (2G)",
      "2": "GPRS (2G)",
      "3": "EDGE (2.5G)",
      "4": "WCDMA (3G)",
      "5": "HSDPA (3.5G)",
      "6": "LTE (4G)",
      "7": "5G NR"
    };
    return types[rat] || "Unknown";
  };

  // Image to display - use thumbnailUrl if available, otherwise imageUrl
  const displayImage = thumbnailUrl || imageUrl;

  // Extract battery data safely
  const batteryData = deviceData?.battery ? JSON.parse(JSON.stringify(deviceData.battery)) : {};
  const batterySoC = batteryData?.batterySoC || "N/A";
  const batteryLevel = batteryData?.batteryLevel || "N/A";
  const batteryVoltage = batteryData?.batteryVoltage || "N/A";

  // Extract cellular data safely - updated to match the correct field names
  const cellularData = deviceData?.cellular ? JSON.parse(JSON.stringify(deviceData.cellular)) : {};
  const networkType = cellularData?.cellularRat || "N/A";
  const signalStrength = cellularData?.cellularStrength || "N/A";
  const signalQuality = cellularData?.cellularQuality || "N/A";

  // Extract GNSS data safely and derive location values from it
  const gnssData = deviceData?.gnss ? JSON.parse(JSON.stringify(deviceData.gnss)) : {};
  const latitude = gnssData?.latitude || "N/A";
  const longitude = gnssData?.longitude || "N/A";
  const hdop = gnssData?.hdop || "N/A";
  return <div className="space-y-4">
      {/* Header with View Full Details link */}
      {onViewFullDetails && (
        <div className="flex justify-between items-center p-3 border rounded-lg bg-muted/20">
          <div>
            <h3 className="font-medium">Quick Edit</h3>
            <p className="text-sm text-muted-foreground">Edit basic device settings</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onViewFullDetails}
            className="text-xs"
            style={{ backgroundColor: "#DFEF97", color: "black" }}
          >
            View Full Details →
          </Button>
        </div>
      )}
      
      <div className="p-4 border rounded-lg bg-muted/50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column - Form inputs */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Device Name</label>
              <Input value={deviceName} onChange={e => setDeviceName(e.target.value)} placeholder="Enter device name" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Images Allowed</label>
              <Input 
                type="number"
                min="0"
                value={imagesAllowed} 
                onChange={e => setImagesAllowed(e.target.value)} 
                placeholder="Enter number of images" 
              />
            </div>
            {isSuperuser && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Organisation</label>
                <Select value={organizationId} onValueChange={setOrganizationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organisation" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover max-h-72">
                    <SelectItem value={NO_ORG}>None (unassigned)</SelectItem>
                    {sortOrganisations(organizations, org => org.name).map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
                <label className="text-sm font-medium">Camera Image Quality</label>
                <Select value={cameraImageQuality} onValueChange={setCameraImageQuality}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select image quality" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="CAM_IMAGE_MODE_SVGA">
                      CAM_IMAGE_MODE_SVGA (800×600)
                    </SelectItem>
                    <SelectItem value="CAM_IMAGE_MODE_HD">
                      CAM_IMAGE_MODE_HD (1280×720)
                    </SelectItem>
                    <SelectItem value="CAM_IMAGE_MODE_UXGA">
                      CAM_IMAGE_MODE_UXGA (1600×1200)
                    </SelectItem>
                    <SelectItem value="CAM_IMAGE_MODE_FHD">
                      CAM_IMAGE_MODE_FHD (1920×1080)
                    </SelectItem>
                    <SelectItem value="CAM_IMAGE_MODE_QXGA">
                      CAM_IMAGE_MODE_QXGA (2048×1536)
                    </SelectItem>
                    <SelectItem value="CAM_IMAGE_MODE_WQXGA2">
                      CAM_IMAGE_MODE_WQXGA2 (2592×1944)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            
            <div className="flex items-start gap-3">
              <label className="text-sm font-medium pr-4 pt-1">Enable Debug</label>
              <Switch
                checked={enableDebug}
                onCheckedChange={handleDebugToggle}
                disabled={isUpdatingDebug}
              />
            </div>
          </div>
          
          {/* Right column - Image thumbnail */}
          <div className="flex flex-col items-center justify-start">
            <h3 className="text-sm font-medium mb-2">Latest Device Image</h3>
            <div className="rounded-md overflow-hidden border border-border w-full max-w-[250px] bg-muted/30">
              {displayImage ? <AspectRatio ratio={4 / 3.75} className="bg-muted">
                  <img src={displayImage} alt="Device" className="w-full h-full object-cover" />
                </AspectRatio> : <AspectRatio ratio={4 / 3.75} className="bg-muted flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">No image available</p>
                </AspectRatio>}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Device ID: {deviceId}</p>
            
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} style={{ backgroundColor: "#DFEF97", color: "black" }}>Save</Button>
        </div>
      </div>

      {/* Device Information Cards - Three Columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Battery Status Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="pb-2">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <Battery className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Battery Status</CardTitle>
                <CardDescription className="text-blue-700">
                  Current device power information
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Charge Level:</span>
                <span className="font-medium">{batteryLevel}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Battery SoC:</span>
                <span className="font-medium">{batterySoC}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Voltage:</span>
                <span className="font-medium">{batteryVoltage}V</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cellular Status Card */}
        <Card className="bg-violet-50 border-violet-200">
          <CardHeader className="pb-2">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-violet-100">
                <Signal className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-base">Cellular Status</CardTitle>
                <CardDescription className="text-violet-700">
                  Network connection details
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Network Type:</span>
                <span className="font-medium">{getCellularNetworkType(networkType)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Signal Strength:</span>
                <span className="font-medium">{signalStrength}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Signal Quality:</span>
                <span className="font-medium">{signalQuality}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Card */}
        <Card className="bg-green-50 border-green-200">
          <CardHeader className="pb-2">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <MapPin className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-base">Location</CardTitle>
                <CardDescription className="text-green-700">
                  Current device coordinates
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Latitude:</span>
                <span className="font-medium">{latitude !== "N/A" ? `${latitude}°` : latitude}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Longitude:</span>
                <span className="font-medium">{longitude !== "N/A" ? `${longitude}°` : longitude}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">HDOP:</span>
                <span className="font-medium">{hdop}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Debug Data Card */}
      <Card className="bg-yellow-50 border-yellow-200 transition-all duration-300">
        <CardHeader 
          className="pb-2 cursor-pointer" 
          onClick={() => setIsDebugExpanded(!isDebugExpanded)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3" onClick={() => setIsDebugExpanded(!isDebugExpanded)}>
              <div className="p-2 rounded-full bg-yellow-100">
                <Bug className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <CardTitle className="text-base">Debug Data</CardTitle>
                <CardDescription className="text-yellow-700">
                  View detailed debug information and device diagnostics.
                </CardDescription>
              </div>
            </div>
            <div className="p-1">
              {isDebugExpanded ? (
                <ChevronUp className="h-4 w-4 text-yellow-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-yellow-600" />
              )}
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              size="default"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                fetchDebugData();
              }}
              disabled={isLoadingDebug}
              className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingDebug ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent 
          className={`transition-all duration-300 overflow-hidden ${
            isDebugExpanded ? 'max-h-[36rem] overflow-y-auto' : 'max-h-24'
          }`}
        >
          <div className="space-y-3">
            {parsedDebugData.length > 0 ? (
              <div className="text-sm text-yellow-700">
                Device debug stream ({parsedDebugData.length} entries) - most recent first:
              </div>
            ) : (
              <div className="text-sm text-yellow-700">
                {isLoadingDebug ? "Loading debug data..." : "Debug information will be displayed here when available."}
              </div>
            )}
            
            {parsedDebugData.length > 0 && (
              <div className="space-y-1">
                <div className="p-3 bg-yellow-100 rounded-md overflow-y-auto overflow-x-hidden">
                  <div className="space-y-1 text-xs font-mono break-all whitespace-pre-wrap max-w-full">
                    {parsedDebugData.map((entry, index) => {
                      // Get user's local timezone
                      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                      
                      // Convert UTC timestamp to local time
                      const timeStr = formatInTimeZone(
                        new Date(entry.timestamp),
                        userTimezone,
                        'dd/MM/yyyy HH:mm:ss'
                      );
                      
                      return (
                        <div key={index} className="text-yellow-800 border-l-2 border-yellow-300 pl-2 py-1 overflow-hidden">
                          <span className="text-yellow-600 font-medium">{timeStr}</span>
                          <span className="ml-2 text-yellow-700 break-all">{entry.message}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Credit History Card */}
      <DeviceCreditHistory deviceId={deviceId} autoLoad={false} />

      {/* Portal Log Data Card */}
      <Card className="bg-sky-50 border-sky-200 transition-all duration-300">
        <CardHeader 
          className="pb-2 cursor-pointer" 
          onClick={() => {
            const newExpanded = !isPortalLogExpanded;
            setIsPortalLogExpanded(newExpanded);
            if (newExpanded && portalLogs.length === 0) fetchPortalLogs();
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-sky-100">
                <ClipboardList className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <CardTitle className="text-base">Portal Change Log</CardTitle>
                <CardDescription className="text-sky-700">
                  View a history of changes made to this device via the portal.
                </CardDescription>
              </div>
            </div>
            <div className="p-1">
              {isPortalLogExpanded ? (
                <ChevronUp className="h-4 w-4 text-sky-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-sky-600" />
              )}
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              size="default"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                fetchPortalLogs();
              }}
              disabled={isLoadingPortalLogs}
              className="bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingPortalLogs ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent 
          className={`transition-all duration-300 overflow-hidden ${
            isPortalLogExpanded ? 'max-h-[36rem]' : 'max-h-0 p-0'
          }`}
        >
          {isLoadingPortalLogs ? (
            <div className="text-sm text-sky-700 py-2">Loading change logs...</div>
          ) : portalLogs.length === 0 ? (
            <div className="text-sm text-sky-700 py-2">No change logs found for this device.</div>
          ) : (
            <div className="overflow-y-auto max-h-[30rem] pr-2">
              <div className="space-y-1">
                <div className="text-sm text-sky-700 mb-2">
                  {portalLogs.length} change{portalLogs.length !== 1 ? 's' : ''} recorded — most recent first:
                </div>
                <div className="p-3 bg-sky-100 rounded-md">
                  <div className="space-y-2 text-xs font-mono">
                    {portalLogs.map((log, index) => {
                      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                      const timeStr = formatInTimeZone(
                        new Date(log.created_at),
                        userTimezone,
                        'dd/MM/yyyy HH:mm:ss'
                      );
                      return (
                        <div key={index} className="text-sky-800 border-l-2 border-sky-300 pl-2 py-1">
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                            <span className="text-sky-600 font-medium">{timeStr}</span>
                            <span className="text-sky-900 font-semibold">{log.field}</span>
                            <span className="text-sky-700">→ {log.value || '(empty)'}</span>
                          </div>
                          <div className="flex gap-x-3 text-sky-600 mt-0.5">
                            <span>by {log.user_name}</span>
                            <span>• {log.org_name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Image Counter Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <RefreshCw className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-base">Reset Image Counter</CardTitle>
              <CardDescription className="text-amber-700">
                Reset the image counter back to zero. This will not delete any images.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setIsResetDialogOpen(true)} className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200 gap-2">
              <RefreshCw className="h-4 w-4" />
              Reset Counter to Zero
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete SD Card Files Card */}
      <Card className="bg-orange-50 border-orange-200">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-orange-100">
              <Trash2 className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <CardTitle className="text-base">Delete SD Card Files</CardTitle>
              <CardDescription className="text-orange-700">
                Delete all images from the device's SD card. This will free up storage on the device.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setIsDeleteFileDialogOpen(true)} className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200 gap-2">
              <Trash2 className="h-4 w-4" />
              Delete SD Card Files
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Archive Card */}
      <Card className="bg-destructive/10 border-destructive/20">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-destructive/20">
              <ArchiveX className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-base">Archive Device Images</CardTitle>
              <CardDescription className="text-destructive/70">
                This will archive all images from this device and prepare it for a new customer. 
                This process cannot be undone.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end">
            <Button variant="destructive" onClick={() => setIsArchiveDialogOpen(true)} className="gap-2">
              <ArchiveX className="h-4 w-4" />
              Archive Images
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Remove Assignments Card */}
      <Card className="bg-red-50 border-red-200">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-red-100">
              <UserX className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-base">Remove Device Assignments</CardTitle>
              <CardDescription className="text-red-700">
                Remove all user and organization assignments, credit packages, and allocations for this device. 
                Use this to prepare the device for a new customer assignment.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsRemoveAssignmentsDialogOpen(true)} 
              className="bg-red-100 text-red-800 border-red-200 hover:bg-red-200 gap-2"
            >
              <UserX className="h-4 w-4" />
              Remove Assignments
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reset Counter Confirmation Dialog */}
      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Image Counter?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset the image counter back to zero, but will not delete any images.
              Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetCounter}
              style={{ backgroundColor: '#DFEF97', color: 'black' }}
            >
              Reset Counter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete SD Card Files Confirmation Dialog */}
      <AlertDialog open={isDeleteFileDialogOpen} onOpenChange={setIsDeleteFileDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SD Card Files?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all images from the device's SD card. 
              This action cannot be undone. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSdCardFiles}
              style={{ backgroundColor: '#DFEF97', color: 'black' }}
            >
              Delete Files
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={isArchiveDialogOpen} onOpenChange={(open) => {
        setIsArchiveDialogOpen(open);
        if (!open) setArchiveConfirmationText("");
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Archive Device Images: {deviceName || deviceId}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="text-sm">
                <strong>⚠️ WARNING:</strong> This will archive all images from this device and prepare it for a new customer. 
                This process cannot be undone.
              </p>
              <p className="text-sm">
                To confirm this action, please type either <strong>"ARCHIVE"</strong> or the exact device name <strong>"{deviceName || deviceId}"</strong> below:
              </p>
              <div className="space-y-2">
                <Input
                  value={archiveConfirmationText}
                  onChange={(e) => setArchiveConfirmationText(e.target.value)}
                  placeholder={`Type "ARCHIVE" or "${deviceName || deviceId}"`}
                  className={archiveConfirmationText && !isValidArchiveConfirmation(archiveConfirmationText) ? "border-destructive" : ""}
                  autoFocus
                />
                {archiveConfirmationText && !isValidArchiveConfirmation(archiveConfirmationText) && (
                  <p className="text-xs text-destructive">
                    Please type exactly "ARCHIVE" or "{deviceName || deviceId}"
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setArchiveConfirmationText("")}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleArchive}
              disabled={!isValidArchiveConfirmation(archiveConfirmationText)}
              style={{ backgroundColor: '#DFEF97', color: 'black' }}
            >
              Confirm Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Assignments Confirmation Dialog */}
      <AlertDialog open={isRemoveAssignmentsDialogOpen} onOpenChange={(open) => {
        setIsRemoveAssignmentsDialogOpen(open);
        if (!open) setRemoveAssignmentsConfirmationText("");
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              Remove All Assignments: {deviceName || deviceId}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="text-sm">
                <strong>⚠️ WARNING:</strong> This will remove all assignments and credits associated with this device:
              </p>
              <ul className="text-sm list-disc list-inside space-y-1 text-muted-foreground">
                <li>All user and organization device assignments</li>
                <li>All credit packages assigned to this device</li>
                <li>All credit allocations for this device</li>
              </ul>
              <p className="text-sm">
                This action prepares the device for a new customer but <strong>does NOT</strong> archive images or change device settings.
              </p>
              <p className="text-sm">
                To confirm this action, please type either <strong>"REMOVE"</strong> or the exact device name <strong>"{deviceName || deviceId}"</strong> below:
              </p>
              <div className="space-y-2">
                <Input
                  value={removeAssignmentsConfirmationText}
                  onChange={(e) => setRemoveAssignmentsConfirmationText(e.target.value)}
                  placeholder={`Type "REMOVE" or "${deviceName || deviceId}"`}
                  className={removeAssignmentsConfirmationText && !isValidRemoveAssignmentsConfirmation(removeAssignmentsConfirmationText) ? "border-red-500" : ""}
                  autoFocus
                  disabled={isRemovingAssignments}
                />
                {removeAssignmentsConfirmationText && !isValidRemoveAssignmentsConfirmation(removeAssignmentsConfirmationText) && (
                  <p className="text-xs text-red-600">
                    Please type exactly "REMOVE" or "{deviceName || deviceId}"
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => setRemoveAssignmentsConfirmationText("")}
              disabled={isRemovingAssignments}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveAssignments}
              disabled={!isValidRemoveAssignmentsConfirmation(removeAssignmentsConfirmationText) || isRemovingAssignments}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isRemovingAssignments ? "Removing..." : "Confirm Removal"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};